import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Plus, Clock } from "lucide-react";

import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { PageBreadcrumb } from "@/components/layout/PageBreadcrumb.tsx";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  getOrderById,
  formatTotal,
  extractCustomerTagValue,
  extractCashDiscountPercent,
  parseItemOptions,
  type OrderRecord,
} from "@/lib/orders";
import {
  fetchOrderById,
  fetchProducts,
  fetchCustomerById,
  fetchCustomers,
  parseDiscountPercentFromGroupName,
} from "@/lib/api";
import { getProductAvailability } from "@/lib/availability";
import { type ClientRecord } from "@/lib/mockClients";

// ----------------------------------------------------------------------------

interface OrdineCompletatoProps {
  className?: string;
}

export function OrdineCompletato(props: OrdineCompletatoProps) {
  // -------------------------------------
  // Props destructuring
  // -------------------------------------

  const { className } = props;

  // -------------------------------------
  // Hooks (e.g. useState, useMemo ...)
  // -------------------------------------

  const { id } = useParams();
  const navigate = useNavigate();

  // -------------------------------------
  // Component state & variables
  // -------------------------------------

  const orderId = id || "";
  const [order, setOrder] = React.useState<OrderRecord | undefined>(() =>
    id ? getOrderById(id) : undefined,
  );
  const [isLoadingOrder, setIsLoadingOrder] = React.useState(!order);
  const [client, setClient] = React.useState<ClientRecord | null>(null);

  // Load from backend if not available in localStorage
  React.useEffect(() => {
    let isMounted = true;
    if (!id) {
      setIsLoadingOrder(false);
      return;
    }

    if (!order) {
      setIsLoadingOrder(true);
      fetchOrderById(id)
        .then((fetched) => {
          if (isMounted && fetched) {
            setOrder(fetched);
          }
        })
        .catch((err) => {
          console.warn("[OrdineCompletato] Failed to fetch order by ID:", err);
        })
        .finally(() => {
          if (isMounted) setIsLoadingOrder(false);
        });
    } else {
      setIsLoadingOrder(false);
    }
    return () => {
      isMounted = false;
    };
  }, [id, order]);

  React.useEffect(() => {
    if (!order) return;
    let isMounted = true;

    async function loadCustomer() {
      const identifier = order?.clientId || order?.email;
      if (!identifier) return;
      try {
        const found = await fetchCustomerById(identifier);
        if (isMounted && found) {
          setClient(found);
        }
      } catch (err) {
        console.warn("[OrdineCompletato] Failed to fetch customer:", err);
      }
    }

    void loadCustomer();
    return () => {
      isMounted = false;
    };
  }, [order?.clientId, order?.email]);

  // Enrich item tags if missing
  React.useEffect(() => {
    if (!order || order.items.length === 0) return;
    const needsTags = order.items.some((it) => !it.tags || it.tags.length === 0);
    if (!needsTags) return;

    let isMounted = true;
    async function enrichItems() {
      try {
        const updated = await Promise.all(
          order!.items.map(async (item) => {
            if (item.tags && item.tags.length > 0) return item;
            try {
              const res = await fetchProducts(item.code);
              const matched = res.products.find(
                (p) => p.sku === item.code || p.code === item.code,
              );
              if (matched) {
                return {
                  ...item,
                  tags: matched.tags,
                  stockCount: matched.stockCount,
                  unlimited: matched.unlimited,
                  imageUrl: item.imageUrl || matched.imageUrl,
                };
              }
            } catch {
              // ignore
            }
            return item;
          }),
        );
        if (isMounted) {
          setOrder((prev) => (prev ? { ...prev, items: updated } : prev));
        }
      } catch {
        // ignore
      }
    }

    void enrichItems();
    return () => {
      isMounted = false;
    };
  }, [order?.id]);

  const clientName = client?.name || order?.customerName || "";
  const clientEmail = client?.email || order?.email || "";

  const cashDiscountPercent =
    order?.cashDiscountPercent ??
    extractCashDiscountPercent(order?.customerTags) ??
    extractCashDiscountPercent(client?.tags);

  const itemsSubtotal = React.useMemo(() => {
    return (order?.items || []).reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [order?.items]);

  const cashDiscountAmount = React.useMemo(() => {
    if (!cashDiscountPercent || cashDiscountPercent <= 0) return 0;
    return (itemsSubtotal * cashDiscountPercent) / 100;
  }, [itemsSubtotal, cashDiscountPercent]);

  const discountPercent =
    client?.discountPercent ??
    order?.customerDiscountPercent ??
    (client?.groupName ? parseDiscountPercentFromGroupName(client.groupName) : undefined);

  const discountText =
    discountPercent !== undefined
      ? `Sconto ${discountPercent}%`
      : client?.groupName
      ? client.groupName
      : "Sconto 0%";

  const paymentTagValue =
    extractCustomerTagValue(client?.tags, "payment") ||
    order?.paymentTag ||
    extractCustomerTagValue(order?.customerTags, "payment") ||
    "-";

  const paydescriptionTagValue =
    extractCustomerTagValue(client?.tags, "paydescription") ||
    order?.paydescriptionTag ||
    extractCustomerTagValue(order?.customerTags, "paydescription") ||
    "-";

  return (
    <DashboardLayout className={className}>
      <div className="w-full">
        <PageBreadcrumb
          items={[{ label: "Home", to: "/" }, { label: `Ordine ID ${orderId || "-"}` }]}
        />

        {/* Main Card */}
        <Card className="shadow-sm border-gray-100 max-w-2xl">
          <CardHeader className="pb-6">
            <CardTitle className="text-2xl font-bold">
              Ordine completato
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {isLoadingOrder ? (
              <div className="py-12 text-center text-sm text-gray-500">
                Caricamento dati ordine in corso...
              </div>
            ) : !order ? (
              <div className="py-12 text-center text-sm text-gray-500">
                Nessun dato trovato per l'ordine specificato.
              </div>
            ) : (
              <>
                <div className="space-y-1">
                  <p className="text-gray-900 font-medium">ID ordine: #{order.id}</p>
                  {(clientName || clientEmail) && (
                    <p className="text-gray-900 font-medium">
                      Cliente: {clientName ? `${clientName} ${clientEmail ? `(${clientEmail})` : ""}` : clientEmail}
                    </p>
                  )}
                  <div className="pt-2 text-sm space-y-1 text-gray-700 border-t border-gray-100 mt-2">
                    <p className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-gray-900">Scontistica:</span>{" "}
                      <span>{discountText}</span>
                      {cashDiscountPercent !== undefined && cashDiscountPercent > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                          Sconto piede: {cashDiscountPercent}%
                        </span>
                      )}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-900">Pagamento:</span>{" "}
                      {paymentTagValue}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-900">Descrizione pagamento:</span>{" "}
                      {paydescriptionTagValue}
                    </p>
                  </div>
                </div>

                <div className="space-y-4">
                  <p className="font-bold text-gray-900">Prodotti ({order.items.length}):</p>

                  <div className="pl-4 space-y-6">
                    {order.items.length === 0 ? (
                      <p className="text-xs text-gray-500 italic">Nessun articolo presente</p>
                    ) : (
                      order.items.map((item) => {
                        const availability = getProductAvailability({
                          tags: item.tags,
                          stockCount: item.stockCount,
                          unlimited: item.unlimited,
                        });
                        const isRestocking = availability.caseType === "restocking";

                        return (
                          <div key={item.id} className="flex justify-between items-start">
                            <div className="flex-1 pr-4">
                              <p className="font-bold text-gray-900 mb-1">{item.code}</p>
                              {(() => {
                                const parsed = parseItemOptions(item.options || item.note);
                                const color =
                                  item.attributes?.color && item.attributes.color !== "Standard"
                                    ? item.attributes.color
                                    : parsed.attributes.color;
                                const temp =
                                  item.attributes?.temperature && item.attributes.temperature !== "Standard"
                                    ? item.attributes.temperature
                                    : parsed.attributes.temperature;
                                const size =
                                  item.attributes?.size && item.attributes.size !== "Standard"
                                    ? item.attributes.size
                                    : parsed.attributes.size;
                                const displayNote =
                                  item.note && item.note !== item.options
                                    ? item.note
                                    : parsed.note || (item.note && !item.note.includes("Colore:") ? item.note : undefined);

                                return (
                                  <>
                                    <div className="text-xs text-gray-500 space-y-0.5">
                                      <p>Quantità: {item.quantity}</p>
                                      {color && <p>Colore: {color}</p>}
                                      {temp && <p>Temperatura: {temp}</p>}
                                      {size && <p>Misura: {size}</p>}
                                    </div>

                                    {/* Badge Riassortimento con Data di Presunto Arrivo */}
                                    {isRestocking && (
                                      <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 border border-amber-200 text-amber-950 text-xs font-medium">
                                        <Clock className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                                        <span>
                                          In riassortimento — Data presunto arrivo:{" "}
                                          <strong className="font-bold text-amber-950">
                                            {availability.restockDate || "In attesa"}
                                          </strong>
                                          {availability.restockQuantity !== undefined && (
                                            <span className="text-amber-800 text-[11px] ml-1">
                                              ({availability.restockQuantity} pz previsti)
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                    )}

                                    {displayNote && (
                                      <div className="mt-2 text-xs text-gray-600 bg-gray-50 p-2 rounded border border-gray-100">
                                        <span className="font-semibold text-gray-700">Nota articolo:</span>{" "}
                                        {displayNote}
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                            <span className="text-xs font-medium shrink-0">{formatTotal(item.price * item.quantity)}</span>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {/* Note Ordine */}
                {(order.notes || order.message) && (
                  <div className="pt-4 border-t border-gray-100 space-y-1.5">
                    <p className="font-bold text-gray-900 text-sm">Note generali ordine:</p>
                    <div className="text-xs text-gray-700 bg-gray-50 p-3 rounded-lg border border-gray-100 whitespace-pre-wrap leading-relaxed">
                      {order.notes || order.message}
                    </div>
                  </div>
                )}

                {cashDiscountPercent !== undefined && cashDiscountPercent > 0 ? (
                  <div className="space-y-2.5 pt-4 mt-6 border-t border-gray-100">
                    <div className="flex justify-between items-center text-sm text-gray-600">
                      <span>Subtotale articoli</span>
                      <span className="font-medium text-gray-900">{formatTotal(itemsSubtotal)}</span>
                    </div>
                    <div className="flex justify-between items-center text-sm text-emerald-700">
                      <span className="flex items-center gap-1.5">
                        <span>Sconto piede</span>
                        <span className="text-[11px] font-semibold bg-emerald-50 px-1.5 py-0.2 rounded border border-emerald-200">
                          -{cashDiscountPercent}%
                        </span>
                      </span>
                      <span className="font-semibold">-{formatTotal(cashDiscountAmount)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                      <span className="font-bold text-gray-900 text-lg">Totale ordine</span>
                      <span className="font-bold text-gray-900 text-xl">
                        {order.total}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="flex justify-between items-center pt-6 mt-6 border-t border-gray-100">
                    <span className="font-bold text-gray-900 text-lg">Totale</span>
                    <span className="font-bold text-gray-900 text-xl">
                      {order.total}
                    </span>
                  </div>
                )}
              </>
            )}
          </CardContent>

          <CardFooter className="flex justify-between gap-4 pt-4 border-t border-gray-100">
            <Button
              variant="outline"
              className="flex-1 text-gray-900 font-medium border-gray-200"
              onClick={() => navigate("/ordini")}
            >
              Tutti gli ordini
            </Button>
            <Button
              className="flex-1 bg-[#0D6EFD] hover:bg-blue-700 text-white font-medium"
              onClick={() => navigate("/nuovo-ordine")}
            >
              <div className="w-4 h-4 rounded-full bg-white/20 flex items-center justify-center mr-2">
                <Plus className="w-3 h-3 text-white" />
              </div>
              Nuovo ordine
            </Button>
          </CardFooter>
        </Card>
      </div>
    </DashboardLayout>
  );
}
